import { noop } from 'lodash';
import { createContext, Dispatch, SetStateAction } from 'react';
import * as React from 'react';

import { useGuardedContext } from '@/hooks/useGuardedContext';
import {
  AISuggestionMatch,
  AISuggestionsMatcherArrayField,
  AISuggestionsMatcherVariant,
  AISuggestionsMatcherVariantConfig,
} from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcher.types';

export interface AiSuggestionsMatcherContext {
  // The variant of the AI suggestions matcher. E.g. 'grantors', 'trustees', etc.
  matcherVariant: AISuggestionsMatcherVariant | null;
  setMatcherVariant: Dispatch<
    SetStateAction<AiSuggestionsMatcherContext['matcherVariant']>
  >;

  // Matched suggestions are the "uncommitted" suggestions that the user has
  // matched for the current entity section. These are committed when the user
  // clicks the "Update form with identified suggestions" button, or can be
  // automatically "committed" when the user saves the entity. Committed
  // suggestions are shown in the form.
  matchedSuggestions: Record<string, AISuggestionMatch>;
  setMatchedSuggestions: Dispatch<
    SetStateAction<AiSuggestionsMatcherContext['matchedSuggestions']>
  >;

  // Bulk commits matched or ignored suggestions to the form, and acknowledges
  // each suggestion as accepted or rejected. Accepted suggestions will be
  // removed from the matchedSuggestions state, while rejected suggestions will
  // be acknowledged and not shown in the UI again.
  handleCommitMatchedSuggestions: (
    matchedSuggestions: Record<string, AISuggestionMatch>
  ) => void;

  // Commits the matched suggestion to the form, and acknowledges the suggestion
  // as accepted. This will remove the suggestion from the matchedSuggestions state.
  confirmRow: (suggestionId: string) => void;

  // Commits the rejected suggestion, and acknowledges the suggestion as rejected.
  ignoreRow: (suggestionId: string) => void;

  // The ref to the AISuggestionsMatcher component.
  matcherRef: React.RefObject<HTMLDivElement | null>;

  // Various configs for the matcher component.
  matcherConfigs: AISuggestionsMatcherVariantConfig;

  // The IDs of things that were added by accepting suggestions from the matcher.
  newlyAddedIDs: string[];
  setNewlyAddedIDs: Dispatch<
    SetStateAction<AiSuggestionsMatcherContext['newlyAddedIDs']>
  >;

  // The array fields that have duplicates mapped to the number of duplicates.
  arrayFieldsWithDuplicates: Partial<
    Record<AISuggestionsMatcherArrayField, number>
  >;
  setArrayFieldsWithDuplicates: Dispatch<
    SetStateAction<AiSuggestionsMatcherContext['arrayFieldsWithDuplicates']>
  >;

  // This is called from the duplicate resolution card when the user clicks the
  // item to keep. This will remove the other item from the form.
  resolveDuplicate: (
    arrayFieldFullPath: AISuggestionsMatcherArrayField
  ) => void;

  // Tracks which item is selected in each duplicate group and its onConfirm callback
  duplicateSelections: Partial<
    Record<
      AISuggestionsMatcherArrayField,
      {
        selectedIndex: number;
        onConfirm: () => void;
      }
    >
  >;
  setDuplicateSelections: Dispatch<
    SetStateAction<AiSuggestionsMatcherContext['duplicateSelections']>
  >;
}

export const getDefaultValues = (): AiSuggestionsMatcherContext => ({
  matcherVariant: null,
  setMatcherVariant: noop,
  matchedSuggestions: {},
  setMatchedSuggestions: noop,
  handleCommitMatchedSuggestions: noop,
  confirmRow: noop,
  ignoreRow: noop,
  matcherRef: { current: null } as React.RefObject<HTMLDivElement | null>,
  matcherConfigs: getDefaultMatcherConfigs(),
  newlyAddedIDs: [],
  setNewlyAddedIDs: noop,
  arrayFieldsWithDuplicates: {},
  setArrayFieldsWithDuplicates: noop,
  resolveDuplicate: noop,
  duplicateSelections: {},
  setDuplicateSelections: noop,
});

export const getDefaultMatcherConfigs =
  (): AISuggestionsMatcherVariantConfig => ({
    nounSingular: '',
    nounPlural: '',
    suggestionKinds: [],
    addNewAnythingModalProps: {},
    subformArrayFieldUpdateProps: [],
    subformArrayFieldOnConflict: 'overwrite',
  });

export const AiSuggestionsMatcherContext =
  createContext<AiSuggestionsMatcherContext>(getDefaultValues());

export const useAiSuggestionsMatcherContext = () => {
  return useGuardedContext(
    AiSuggestionsMatcherContext,
    'AiSuggestionsMatcherContextProvider'
  );
};
